import React, { useEffect, useState } from 'react';
import { CheckCircle2, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { adminApi } from '../../api/client';
import DashModal from './DashModal';
import StatusBadge from './StatusBadge';
import './AssignTranslatorForm.css';

const REASON_LABELS = {
  unavailable: 'Indisponible',
  category: 'Type non compatible',
  languages: 'Langues non couvertes',
  no_urgent: 'N’accepte pas les urgences',
  overloaded: 'Surchargé',
  inactive: 'Inactif',
};

const AVAILABILITY_LABELS = {
  available: 'Disponible',
  busy: 'Occupé',
  unavailable: 'Indisponible',
};

const PRIORITY_LABELS = { normal: 'Normal', high: 'Rapide', urgent: 'Urgent' };

// Affiche la paire couverte dans le sens source→cible de la commande.
function orderPairLabel(c, order) {
  const p = c.translatorProfile || {};
  const src = String(order.sourceLang || '').toUpperCase();
  const tgt = String(order.targetLang || '').toUpperCase();
  if (coversPairClient(p, src, tgt)) return `${order.sourceLang} → ${order.targetLang}`;
  return c.langs;
}

// Même logique que coversPair côté backend : le profil couvre-t-il la paire src→tgt ?
function coversPairClient(p, src, tgt) {
  const S = String(src || '').toUpperCase();
  const T = String(tgt || '').toUpperCase();
  if (!S || !T) return false;
  for (const x of [...(p.languageCombos || []), ...(p.langPairs || [])]) {
    const s = String(x.source || '').toUpperCase();
    const t = String(x.target || '').toUpperCase();
    if (s === S && t === T) return true;
    if (x.direction === 'both' && s === T && t === S) return true;
  }
  const srcs = (p.sourceLanguages || []).map((l) => String(l).toUpperCase());
  const tgts = (p.targetLanguages || []).map((l) => String(l).toUpperCase());
  return srcs.includes(S) && tgts.includes(T);
}

export default function AssignTranslatorForm({ order, open, onClose, onDone }) {
  const { t } = useLanguage();
  const d = t.dashboard;
  const a = d.admin;
  const [candidates, setCandidates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!open || !order?.id) return;
    setCandidates([]);
    setSelectedId(null);
    setDone(null);
    setMsg('');
    adminApi.orderCandidates(order.id)
      .then((res) => setCandidates(res.candidates || []))
      .catch(() => setCandidates([]));
  }, [open, order?.id]);

  const compatible = candidates.filter((c) => c.compatible);
  const incompatible = candidates.filter((c) => !c.compatible);
  const selected = candidates.find((c) => c.id === selectedId) || null;
  const suggestedId = order?.suggestedTranslatorId;

  const confirm = async () => {
    if (!selected) return;
    setBusy(true);
    setMsg('');
    try {
      const res = await adminApi.assignOrder(order.id, selectedId);
      setDone({ order: res.order, name: selected.name, method: 'manual' });
      onDone?.();
    } catch {
      setMsg(a.assignError || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const acceptSuggestion = async () => {
    setBusy(true);
    setMsg('');
    try {
      const res = await adminApi.acceptSuggestion(order.id);
      setDone({ order: res.order, name: order.suggestedTranslator, method: 'auto' });
      onDone?.();
    } catch {
      setMsg(a.assignError || 'Error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashModal
      open={open}
      wide
      title={`Affecter un professionnel — ${order?.id || ''}`}
      subtitle={order ? `${order.sourceLang} → ${order.targetLang} · ${order.details?.translationService || order.details?.interpretingType || order.document}${order.pages ? ` · ${order.pages} pages` : ''} · Priorité : ${d.priority?.[order.priority] || PRIORITY_LABELS[order.priority] || order.priority}` : ''}
      onClose={onClose}
    >
      {order && done ? (
        <div>
          <p className="assign-warn" style={{ background: 'rgba(22,163,74,0.1)', color: '#15803d' }}>
            <CheckCircle2 size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
            Professionnel affecté avec succès
          </p>
          <ul className="dash-file-list">
            <li><span>Commande</span><strong>{done.order?.id || order.id}</strong></li>
            <li><span>Professionnel</span><strong>{done.name}</strong></li>
            <li>
              <span>Méthode</span>
              <strong>{done.method === 'auto' ? 'Affectation automatique' : 'Affectation manuelle'}</strong>
            </li>
            <li><span>Date</span><strong>{done.order?.assignedAt || '—'}</strong></li>
            <li><span>Statut</span><StatusBadge status={done.order?.status || 'translator_assigned'} labels={d.status} /></li>
          </ul>
          <div className="dash-actions" style={{ marginTop: 14, marginBottom: 0 }}>
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Retour à la commande
            </button>
          </div>
        </div>
      ) : order ? (
        <div className="assign-form">
          {order.suggestedTranslator && !order.translator && (
            <section className="assign-suggestion">
              <Sparkles size={18} />
              <div>
                <strong>Proposition automatique : {order.suggestedTranslator}</strong>
                <p>Le système a trouvé ce professionnel compatible (langues, service, disponibilité).</p>
              </div>
              <button type="button" className="btn btn-primary" disabled={busy} onClick={acceptSuggestion}>
                Accepter la proposition
              </button>
            </section>
          )}

          <section className="dash-panel" style={{ boxShadow: 'none', padding: '14px 16px' }}>
            <h2 style={{ fontSize: '0.95rem' }}>Professionnels compatibles</h2>
            {candidates.length ? (
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Professionnel</th>
                      <th>Langues</th>
                      <th>Service</th>
                      <th>Disponibilité</th>
                      <th>Expérience</th>
                      <th>{d.actions}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...compatible, ...incompatible].map((c) => {
                      const p = c.translatorProfile || {};
                      const services = [
                        ...(p.services || []),
                        ...(p.translationServices || []),
                        ...(p.interpretingServices || []),
                      ].filter((v, i, arr) => v && arr.indexOf(v) === i);
                      return (
                        <tr
                          key={c.id}
                          className={`${selectedId === c.id ? 'is-selected' : ''}${c.compatible ? '' : ' is-incompatible'}`}
                        >
                          <td>
                            <strong>{c.name}</strong>
                            {suggestedId === c.id && <span className="suggestion-pill" style={{ marginInlineStart: 6 }}>⚡ proposé</span>}
                          </td>
                          <td>{orderPairLabel(c, order)}</td>
                          <td>{services.slice(0, 2).join(', ') || '—'}</td>
                          <td>
                            <span className={`avail-pill avail-pill--${p.availability || 'available'}`}>
                              {AVAILABILITY_LABELS[p.availability] || p.availability || 'Disponible'}
                            </span>
                          </td>
                          <td>{p.experienceYears || 0} an(s)</td>
                          <td>
                            {c.compatible ? (
                              <button
                                type="button"
                                className="dash-btn-sm"
                                onClick={() => setSelectedId(selectedId === c.id ? null : c.id)}
                              >
                                {selectedId === c.id ? 'Sélectionné ✓' : 'Affecter'}
                              </button>
                            ) : (
                              <span className="avail-pill avail-pill--unavailable">
                                {REASON_LABELS[c.reason] || 'Non compatible'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="dash-empty">Aucun professionnel trouvé.</p>
            )}
          </section>

          {selected && (
            <section className="dash-panel assign-selected-panel" style={{ boxShadow: 'none', padding: '14px 16px' }}>
              <h2 style={{ fontSize: '0.95rem' }}>{selected.name}</h2>
              <ul className="dash-file-list">
                <li>
                  <span>Type</span>
                  <strong>{selected.translatorProfile?.professionalType === 'interpreter' ? 'Interprète' : selected.translatorProfile?.professionalType === 'both' ? 'Traductrice & interprète' : 'Traducteur'}</strong>
                </li>
                <li><span>Langues</span><strong>{orderPairLabel(selected, order)}</strong></li>
                {[
                  ...(selected.translatorProfile?.services || []),
                  ...(selected.translatorProfile?.translationServices || []),
                  ...(selected.translatorProfile?.interpretingServices || []),
                ].filter((v, i, arr) => v && arr.indexOf(v) === i).map((s) => (
                  <li key={s}><span>✓</span><strong>{s}</strong></li>
                ))}
                <li><span>Expérience</span><strong>{selected.translatorProfile?.experienceYears || 0} an(s)</strong></li>
                <li><span>Disponibilité</span><strong>{AVAILABILITY_LABELS[selected.translatorProfile?.availability] || 'Disponible'}</strong></li>
                <li><span>Localisation</span><strong>{[selected.city, selected.translatorProfile?.country].filter(Boolean).join(', ') || '—'}</strong></li>
              </ul>
              <div className="dash-actions">
                <button type="button" className="btn btn-primary" disabled={busy} onClick={confirm}>
                  {busy ? '…' : "Confirmer l'affectation"}
                </button>
                <Link className="dash-link" to={`/admin/translators/${selected.id}`}>Voir le profil complet</Link>
              </div>
            </section>
          )}
          {msg && <p className="dash-toast">{msg}</p>}
        </div>
      ) : null}
    </DashModal>
  );
}
